import { Search as SearchIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import dayjs from "dayjs";
import { useAppSelector } from "hooks/useAppSelector";
import { useAuthUser } from "hooks/useAuthUser";
import { useEffect, useState } from "react";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { ROLES } from "types/user";
import { useEducationRecordAccessLog } from "./useEducationRecordAccessLog";

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "read", label: "Read" },
  { value: "export", label: "Export" },
  { value: "write", label: "Write" },
  { value: "delete", label: "Delete" },
  { value: "staff_break_glass", label: "Break-glass" },
];

function formatWhen(value: string): string {
  return dayjs(value).format("MMM D, YYYY h:mm A");
}

export const EducationRecordAccessLog = () => {
  const { role } = useAuthUser();
  const organizationSelected = useAppSelector(organizationSelectedSelector);
  const isAdmin = role === ROLES.ADMIN;

  // Directors/managers always scope to the selected tenant when one is set.
  // Admins without a selected org see the full cross-tenant trail.
  const orgId =
    !isAdmin || organizationSelected?.org_id
      ? organizationSelected?.org_id
      : undefined;

  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [action, setAction] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQ(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const { entries, total, offset, pageSize, loading, error, goToPage } =
    useEducationRecordAccessLog(orgId, {
      q: debouncedQ || undefined,
      action: action || undefined,
    });

  const page = Math.floor(offset / pageSize);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters = Boolean(debouncedQ || action);

  return (
    <MainLayoutContainer
      title="Education record access log"
      description={
        orgId
          ? `Audit trail of staff access to student education records for ${
              organizationSelected?.name || "this organization"
            }.`
          : "Audit trail of staff access to student education records across organizations."
      }
      loading={loading && entries.length === 0 && !hasFilters}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: "center" }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search actor, student, email, resource…"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="access-log-action-label">Action</InputLabel>
          <Select
            labelId="access-log-action-label"
            label="Action"
            value={action}
            onChange={(event) => setAction(event.target.value)}
          >
            {ACTION_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the access log. Try again.
        </Alert>
      )}

      {!loading && !error && total === 0 && (
        <Typography color="text.secondary">
          {hasFilters
            ? "No access events match these filters."
            : `No education-record access events recorded yet${
                orgId ? " for this organization" : ""
              }.`}
        </Typography>
      )}

      {entries.length > 0 && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {offset + 1}–{Math.min(offset + entries.length, total)} of{" "}
            {total}
            {hasFilters ? " matching" : ""}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Resource</TableCell>
                  {!orgId && <TableCell>Organization</TableCell>}
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((row) => (
                  <TableRow key={row.education_record_access_log_id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatWhen(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {row.actor?.name || row.actor_user_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.actor_role}
                          {row.actor?.email ? ` · ${row.actor.email}` : ""}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {row.action}
                    </TableCell>
                    <TableCell>
                      {row.subject ? (
                        <Box>
                          <Typography variant="body2">
                            {row.subject.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.subject.email}
                          </Typography>
                        </Box>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.resource_type}
                      </Typography>
                      {row.resource_id && (
                        <Typography variant="caption" color="text.secondary">
                          {row.resource_id}
                        </Typography>
                      )}
                    </TableCell>
                    {!orgId && (
                      <TableCell>
                        {row.organization?.name || row.org_id || "—"}
                      </TableCell>
                    )}
                    <TableCell>{row.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {pageCount > 1 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                disabled={page <= 0 || loading}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {page + 1} of {pageCount}
              </Typography>
              <Button
                size="small"
                disabled={page + 1 >= pageCount || loading}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </Stack>
      )}
    </MainLayoutContainer>
  );
};
